import React, { useEffect, useState } from "react";
import TabletLayout from "./TabletLayout";
import mesApi from "../../../Api/mes.api";
import { TimelineBatch } from "../../../types/mes";
import { toast, ToastContainer } from "react-toastify";
import { formatClockTime } from "../../../utils/processParameterSerialize";
import { t } from "../../../i18n/locale";

/**
 * Tablet Packaging View - Shows all batches ready for packaging
 * Sorted by baking end time
 */
const TabletPackaging = () => {
  const [batches, setBatches] = useState<TimelineBatch[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBatches();
    const refreshInterval = setInterval(loadBatches, 30000);
    return () => clearInterval(refreshInterval);
  }, []);

  const loadBatches = async () => {
    try {
      const today = new Date().toISOString().split("T")[0];
      // Production-day view: everything physically produced today (ready for
      // packaging), incl. inbound semi-finished made today for other days.
      const { batches: dayBatches } = await mesApi.getProductionDay(today);

      if (dayBatches.length) {
        const packagingBatches = dayBatches
          .filter((b: TimelineBatch) => b.BakingEnd) // Has finished baking
          .sort((a: TimelineBatch, b: TimelineBatch) => {
            const aTime = a.BakingEnd ? new Date(a.BakingEnd).getTime() : 0;
            const bTime = b.BakingEnd ? new Date(b.BakingEnd).getTime() : 0;
            return aTime - bTime;
          });

        setBatches(packagingBatches);
      } else {
        setBatches([]);
      }
    } catch (error: any) {
      toast.error(t("Greška: {message}", { message: error.message }));
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateStr?: string) => formatClockTime(dateStr);

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; color: string }> = {
      completed: { label: "SPREMNO ZA PAKIRANJE", color: "#27ae60" },
      in_progress: { label: "U PAKIRANJU", color: "#17a2b8" },
    };
    const statusInfo = statusMap[status] || { label: status.toUpperCase(), color: "#6c757d" };
    return <span className="status-badge" style={{ background: statusInfo.color }}>{t(statusInfo.label)}</span>;
  };

  return (
    <TabletLayout station={t("Pakiranje")}>
      <ToastContainer position="top-center" autoClose={3000} />
      {loading ? (
        <div className="loading-state"><p>{t("Učitavanje...")}</p></div>
      ) : batches.length === 0 ? (
        <div className="empty-state"><p>{t("Nema gotovih zamjesa")}</p></div>
      ) : (
        <div className="batches-list">
          {batches.map((batch) => (
            <div key={batch.ID} className="batch-card">
              <div className="batch-header">
                <div className="batch-time">{formatTime(batch.BakingEnd)}</div>
                {getStatusBadge(batch.BatchStatus)}
              </div>
              <div className="batch-id">{batch.MixCode}</div>
              <div className="batch-details">
                <div className="detail-item">
                  <span className="detail-label">{t("Proizvod:")}</span>
                  <span className="detail-value">{batch.ProductName || "-"}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">{t("Masa:")}</span>
                  <span className="detail-value">{batch.FinalMassKg?.toFixed(1)} kg</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">{t("Pećnica:")}</span>
                  <span className="detail-value">{batch.OvenName || "-"}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">{t("Gotovo u:")}</span>
                  <span className="detail-value">{formatTime(batch.BakingEnd)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <style>{`
        .batches-list {display: flex; flex-direction: column; gap: 2rem;}
        .batch-card {background: #2a2f3e; border-radius: 12px; padding: 2rem; border-left: 6px solid #27ae60; box-shadow: 0 4px 12px rgba(0,0,0,0.3); transition: all 0.3s;}
        .batch-header {display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem;}
        .batch-time {font-size: 3rem; font-weight: 700; color: #27ae60; font-family: 'Courier New', monospace;}
        .status-badge {padding: 0.75rem 1.5rem; border-radius: 8px; font-size: 1.4rem; font-weight: 700; color: white; letter-spacing: 0.5px;}
        .batch-id {font-size: 2rem; font-weight: 600; color: #ffffff; margin-bottom: 1.5rem; word-break: break-word;}
        .batch-details {display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 1.5rem;}
        .detail-item {display: flex; flex-direction: column; gap: 0.5rem;}
        .detail-label {font-size: 1.1rem; color: #adb3bd; text-transform: uppercase; letter-spacing: 0.5px;}
        .detail-value {font-size: 1.8rem; font-weight: 600; color: #ffffff;}
        .loading-state, .empty-state {display: flex; justify-content: center; align-items: center; height: 60vh; font-size: 2rem; color: #adb3bd;}
      `}</style>
    </TabletLayout>
  );
};

export default TabletPackaging;
